import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { truncateString } from '../../utils';
import { prepItemSelectionChip } from '../../inventory/CountingAndLocations/styles';

export interface SelectionItem {
  id: string;
  name: string;
  groupType?: string; // To identify if the item belongs to 'roles' or 'individuals' group
  userName?: string; // To show in tooltip for individual users
  prepItemId?: string; // To identify if the item is a prep item
  isCustomMetric?: boolean; // Optional flag to change chip color
}

interface SelectionListProps {
  onClearSelection: () => void;
  selectedItems: SelectionItem[];
  onRemoveItem: (itemId: string) => void;
  chipSx?: SxProps<Theme>;
  getChipSx?: (item: SelectionItem) => SxProps<Theme> | undefined;
  sortByName?: boolean;
  chipIcon?: React.ReactElement;
  getChipIcon?: (item: SelectionItem) => React.ReactElement | undefined;
  getChipColor?: (item: SelectionItem) => SelectionListProps['chipColor'] | undefined;
  chipColor?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

const MAX_CHIP_LABEL_LENGTH = 35;

function SelectionList({
  selectedItems,
  onRemoveItem,
  onClearSelection,
  chipSx,
  getChipSx,
  sortByName,
  chipIcon,
  getChipIcon,
  getChipColor,
  chipColor,
}: SelectionListProps) {
  const { t } = useTranslation();
  return (
    <Card sx={{ height: '100%' }}>
      <Grid container padding={2}>
        <Grid item xs={6} alignSelf='center'>
          <Typography variant='subtitle1'>{t('selectionList.currentSelection')}</Typography>
        </Grid>
        <Grid item xs={6} alignItems='right' textAlign='right'>
          <Button variant='text' color='error' onClick={onClearSelection}>
            {t('selectionList.clearSelection')}
          </Button>
        </Grid>
        <Grid item>
          <Typography variant='body2'>
            {t(selectedItems.length === 1 ? 'selectionList.oneItemSelected' : 'selectionList.itemsSelected', {
              numItems: selectedItems.length,
            })}
          </Typography>
        </Grid>
      </Grid>
      <Grid container px={2} pb={2}>
        <Grid item>
          {(sortByName ? [...selectedItems].sort((a, b) => a.name.localeCompare(b.name)) : selectedItems).map(
            (item) => {
              const icon = getChipIcon ? getChipIcon(item) : chipIcon;
              const color = (getChipColor ? getChipColor(item) : undefined) ?? chipColor;
              const perItemSx = getChipSx ? getChipSx(item) : undefined;
              const isIndividualUser = item.groupType === 'individuals';
              const tooltipTitle = (isIndividualUser && item.userName) ?? '';
              const name = (item.name ?? '').trim(); // safe, trimmed string
              const chipLabel = isIndividualUser ? truncateString(name, MAX_CHIP_LABEL_LENGTH) : name;

              const isPrepItem = !!item.prepItemId;
              const chipElement = (
                <Chip
                  key={item.id}
                  data-testid={`selection-chip-${item.id}`}
                  label={chipLabel}
                  color={color}
                  icon={icon}
                  onDelete={() => onRemoveItem(item.id)}
                  style={{ marginRight: '8px', marginBottom: '8px' }}
                  sx={(isPrepItem ? [chipSx, perItemSx, prepItemSelectionChip] : [chipSx, perItemSx]) as SxProps<Theme>}
                />
              );

              return isIndividualUser && tooltipTitle ? (
                <Tooltip key={item.id} title={tooltipTitle} placement='top' arrow>
                  {chipElement}
                </Tooltip>
              ) : (
                chipElement
              );
            }
          )}
        </Grid>
      </Grid>
    </Card>
  );
}

SelectionList.defaultProps = {
  chipSx: undefined,
  getChipSx: undefined,
  sortByName: true,
  chipIcon: undefined,
  getChipIcon: undefined,
  getChipColor: undefined,
  chipColor: 'primary',
};

export default SelectionList;
